import React, { useState, useEffect } from 'react';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import HeaderLogo from './HeaderLogo';
import ReactGA from "react-ga4";
import { HiHome, HiLightningBolt, HiSearch, HiSparkles, HiCurrencyDollar, HiBookOpen } from 'react-icons/hi';

const Header = ({logo}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mouseNearTop, setMouseNearTop] = useState(false);
  const { scrollY } = useScroll();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useMotionValueEvent(scrollY, "change", (latest) => {
    setScrolled(latest > 50);
    setLastScrollY(latest);
  });

  // Header visibility logic
  useEffect(() => {
    const heroHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
    
    if (lastScrollY < heroHeight) {
      // Hero section içindeyiz - header görünür
      setHidden(false);
    } else if (mouseNearTop) {
      // Hero'dan sonra ama mouse üstte - header göster
      setHidden(false);
    } else {
      // Hero'dan sonra ve mouse üstte değil - header gizle
      setHidden(true);
    }
  }, [lastScrollY, mouseNearTop]);

  // Mouse position tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Mouse ekranın üst 80px'ine gelirse header'ı göster
      setMouseNearTop(e.clientY < 80);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on scroll
  useEffect(() => {
    if (mobileMenuOpen) {
      const handleScroll = () => setMobileMenuOpen(false);
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [mobileMenuOpen]);

  const handleRedirect = () => {
    window.location.href = 'https://app.talent14.com/';
    ReactGA.event({
      category: "Navigation",
      action: "Sisteme Giriş Butonu Tıklandı",
      label: "Sisteme Giriş"
    });
  };

  const handleGuideClick = () => {
    navigate('/rehber');
    ReactGA.event({
      category: "Navigation",
      action: "Rehber Butonu Tıklandı",
      label: "Rehber"
    });
  };

  const sectionLinks = [
    { id: 'paths', label: 'Öğrenme Yolculuğu', icon: HiLightningBolt, color: 'from-yellow-400 to-orange-400' },
    { id: 'ai-editor', label: 'Yapay Zeka ', icon: HiSparkles, color: 'from-purple-400 to-pink-400' },
    { id: 'explore', label: 'Keşfet', icon: HiSearch, color: 'from-green-400 to-emerald-400' },
    { id: 'hub', label: 'Topluluk', icon: HiHome, color: 'from-blue-400 to-cyan-400' },
    { id: 'pricing', label: 'Planlar', icon: HiCurrencyDollar, color: 'from-amber-400 to-yellow-400' },
  ];

  const handleSectionClick = (sectionId) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <motion.header 
        className="fixed top-0 left-0 right-0 z-50"
        initial={{ y: -100, opacity: 0 }}
        animate={{ 
          y: hidden ? -100 : 0, 
          opacity: hidden ? 0 : 1 
        }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      >
        {/* Background with blur effect */}
        <motion.div
          className="absolute inset-0 backdrop-blur-xl"
          animate={{
            backgroundColor: scrolled 
              ? 'rgba(15, 23, 42, 0.85)' 
              : 'rgba(15, 23, 42, 0.75)',
          }}
          transition={{ duration: 0.3 }}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-pink-500/20 to-blue-500/20" />
        
        {/* Border */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"
          animate={{
            opacity: scrolled ? 0.3 : 0.1,
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex items-center justify-between px-4 sm:px-6 lg:px-8 xl:px-12 py-2 md:py-2.5 w-full max-w-[1920px] mx-auto">
          {/* Logo */}
          <motion.div 
            className="flex-shrink-0 z-20"
            animate={{
              scale: scrolled ? 0.95 : 1,
            }}
            transition={{ duration: 0.3 }}
          >
            <HeaderLogo logo={logo} scrolled={scrolled} />
          </motion.div>

          {/* Desktop Navigation */}
          {isHome && (
            <nav className="hidden lg:flex flex-1 justify-center items-center gap-1 mx-8">
              <div className="flex items-center gap-1 px-2 py-1.5 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 shadow-lg">
                {sectionLinks.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <motion.button
                      key={link.id}
                      type="button"
                      className="relative px-3 xl:px-4 py-1.5 text-sm xl:text-base font-medium text-white/80 hover:text-white transition-all duration-200 rounded-xl group"
                      onClick={() => handleSectionClick(link.id)}
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        <span className={`bg-gradient-to-br ${link.color} p-1 rounded-lg flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-200`}>
                          <Icon className="text-sm xl:text-base text-white" />
                        </span>
                        <span>{link.label}</span>
                      </span>
                      <motion.div
                        className={`absolute inset-0 bg-gradient-to-r ${link.color} opacity-0 group-hover:opacity-10 rounded-xl`}
                        transition={{ duration: 0.2 }}
                      />
                    </motion.button>
                  );
                })}
              </div>
            </nav>
          )}

          {/* Right side - Mobile menu button and CTA */}
          <div className="flex items-center gap-3 flex-shrink-0 z-20">
            {/* Mobile Menu Button - Her zaman göster (butonlar için) */}
              <motion.button
                type="button"
                className="lg:hidden p-2.5 rounded-xl text-white/90 hover:text-white hover:bg-white/10 transition-all duration-200"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                whileTap={{ scale: 0.9 }}
                aria-label="Menu"
              >
                <motion.div
                  animate={{ rotate: mobileMenuOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {mobileMenuOpen ? (
                      <path d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </motion.div>
              </motion.button>

            {/* Guide Button - Desktop only */}
            <motion.button
              onClick={handleGuideClick}
              className="hidden lg:flex relative px-4 sm:px-5 lg:px-5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold text-sm sm:text-base shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 transition-all duration-300 items-center gap-2 group overflow-hidden"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-400 opacity-0 group-hover:opacity-100"
                transition={{ duration: 0.3 }}
              />
              <HiBookOpen className="relative z-10 text-lg" />
              <span className="relative z-10 whitespace-nowrap">Rehber</span>
            </motion.button>

            {/* CTA Button - Desktop only */}
            <motion.button
              onClick={handleRedirect}
              className="hidden lg:flex relative px-4 sm:px-5 lg:px-5 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold text-sm sm:text-base shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300 items-center gap-2 group overflow-hidden"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 opacity-0 group-hover:opacity-100"
                transition={{ duration: 0.3 }}
              />
              <span className="relative z-10 whitespace-nowrap">Sisteme Giriş</span>
              <motion.span
                className="relative z-10 text-lg"
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                →
              </motion.span>
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.nav
              className="fixed top-[73px] left-0 right-0 z-40 lg:hidden bg-gradient-to-b from-slate-900/98 via-purple-900/95 to-pink-900/90 backdrop-blur-xl border-b border-white/10 shadow-2xl"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <div className="px-4 py-6 space-y-2">
                {/* Section Links - Sadece home sayfasında göster */}
                {isHome && sectionLinks.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <motion.button
                      key={link.id}
                      type="button"
                      className="w-full text-left px-4 py-3.5 rounded-xl text-white/90 hover:text-white hover:bg-white/10 transition-all duration-200 font-medium flex items-center gap-3 group relative overflow-hidden"
                      onClick={() => handleSectionClick(link.id)}
                      whileTap={{ scale: 0.98 }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <span className={`bg-gradient-to-br ${link.color} p-2 rounded-lg shadow-lg relative z-10`}>
                        <Icon className="text-xl text-white drop-shadow-lg" />
                      </span>
                      <span className="relative z-10">{link.label}</span>
                      <motion.span
                        className="ml-auto opacity-0 group-hover:opacity-100 relative z-10"
                        initial={{ x: -10 }}
                        whileHover={{ x: 0 }}
                      >
                        →
                      </motion.span>
                      <motion.div
                        className={`absolute inset-0 bg-gradient-to-r ${link.color} opacity-0 group-hover:opacity-10`}
                        transition={{ duration: 0.2 }}
                      />
                    </motion.button>
                  );
                })}
                
                {/* Action Buttons - Her zaman göster */}
                <div className={`pt-2 ${isHome ? 'border-t border-white/10 mt-2' : ''} space-y-2`}>
                  {/* Guide Button */}
                  <motion.button
                    onClick={() => {
                      handleGuideClick();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold text-base shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 transition-all duration-300 flex items-center justify-center gap-2 group relative overflow-hidden"
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: isHome ? sectionLinks.length * 0.05 + 0.1 : 0.1 }}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-400 opacity-0 group-hover:opacity-100"
                      transition={{ duration: 0.3 }}
                    />
                    <HiBookOpen className="relative z-10 text-xl" />
                    <span className="relative z-10">Rehber</span>
                  </motion.button>

                  {/* CTA Button */}
                  <motion.button
                    onClick={() => {
                      handleRedirect();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold text-base shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300 flex items-center justify-center gap-2 group relative overflow-hidden"
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: isHome ? sectionLinks.length * 0.05 + 0.15 : 0.15 }}
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 opacity-0 group-hover:opacity-100"
                      transition={{ duration: 0.3 }}
                    />
                    <span className="relative z-10">Sisteme Giriş</span>
                    <motion.span
                      className="relative z-10 text-xl"
                      animate={{ x: [0, 4, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    >
                      →
                    </motion.span>
                  </motion.button>
                </div>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
